import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  User, Mail, Phone, Shield, CheckCircle, Loader2, 
  FileText, Car, Building, Calendar, Upload, AlertCircle,
  Lock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const permitTypes = [
  { value: 'B', label: 'Permis B' },
  { value: 'C', label: 'Permis C' },
  { value: 'CE', label: 'Permis CE' },
  { value: 'D', label: 'Permis D' },
  { value: 'EC', label: 'Permis EC' }
];

const DriverProfile = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Personal info
  const [personalData, setPersonalData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  
  // Documents (permit, CNI)
  const [documentsData, setDocumentsData] = useState({
    permitType: '',
    permitNumber: '',
    permitExpirationDate: '',
    cniNumber: ''
  });
  
  // Company info
  const [companyData, setCompanyData] = useState({
    companyName: '',
    rccm: '',
    dfe: '',
    transportCard: ''
  });
  
  // Password
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Charger le profil depuis l'API au montage
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axios.get(`${API}/driver/profile`, {});
        const profile = response.data;
        
        // Mettre à jour les états avec les données de l'API
        setPersonalData({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          phone: profile.phone || '',
          email: profile.email || ''
        });
        
        setDocumentsData({
          permitType: profile.permitType || '',
          permitNumber: profile.permitNumber || '',
          permitExpirationDate: profile.permitExpirationDate || '',
          cniNumber: profile.cniNumber || ''
        });
        
        setCompanyData({
          companyName: profile.companyName || '',
          rccm: profile.rccm || '',
          dfe: profile.dfe || '',
          transportCard: profile.transportCard || ''
        });
        
        // Mettre à jour le contexte user si nécessaire
        if (updateUser) {
          updateUser(profile);
        }
      } catch (error) {
        console.error('Erreur chargement profil:', error);
        // Fallback sur les données du contexte
        setPersonalData({
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          phone: user?.phone || '',
          email: user?.email || ''
        });
        setDocumentsData({
          permitType: user?.permitType || '',
          permitNumber: user?.permitNumber || '',
          permitExpirationDate: user?.permitExpirationDate || '',
          cniNumber: user?.cniNumber || ''
        });
        setCompanyData({
          companyName: user?.companyName || '',
          rccm: user?.rccm || '',
          dfe: user?.dfe || '',
          transportCard: user?.transportCard || ''
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProfile();
  }, []);

  const handlePersonalChange = (e) => {
    setPersonalData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDocumentsChange = (e) => {
    setDocumentsData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCompanyChange = (e) => {
    setCompanyData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e) => {
    setPasswordData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSavePersonal = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/driver/profile`, personalData, {});
      updateUser(personalData);
      setIsEditing(false);
      toast({ title: "✅ Profil mis à jour", description: "Vos informations ont été enregistrées" });
    } catch (error) {
      toast({ 
        title: "Erreur", 
        description: error.response?.data?.detail || "Impossible de sauvegarder",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDocuments = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/driver/documents`, documentsData, {});
      updateUser(documentsData);
      toast({ title: "✅ Documents mis à jour", description: "Vos documents ont été enregistrés" });
    } catch (error) {
      toast({ 
        title: "Erreur", 
        description: error.response?.data?.detail || "Impossible de sauvegarder",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCompany = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/driver/company-docs`, companyData, {});
      updateUser(companyData);
      toast({ title: "✅ Informations société mises à jour" });
    } catch (error) {
      toast({ 
        title: "Erreur", 
        description: error.response?.data?.detail || "Impossible de sauvegarder",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({ title: "Erreur", description: "Les mots de passe ne correspondent pas", variant: "destructive" });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({ title: "Erreur", description: "Le mot de passe doit contenir au moins 6 caractères", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${API}/driver/password`, {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      }, {});
      
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
      toast({ title: "✅ Mot de passe modifié" });
    } catch (error) {
      toast({ 
        title: "Erreur", 
        description: error.response?.data?.detail || "Impossible de changer le mot de passe",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isProfileComplete = () => {
    return documentsData.permitType && documentsData.permitNumber && documentsData.cniNumber;
  };

  // Afficher le loader pendant le chargement
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1db584]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Mon Profil Chauffeur</h1>
          <p className="text-gray-600">Gérez vos informations personnelles et documents</p>
        </div>
        <Badge className={user?.accountType === 'company' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
          {user?.accountType === 'company' ? 'Société de transport' : 'Chauffeur indépendant'}
        </Badge>
      </div>

      {/* Profile completion alert */}
      {!isProfileComplete() && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Profil incomplet</p>
                <p className="text-sm text-amber-700 mt-1">
                  Complétez vos informations de permis et CNI pour pouvoir recevoir des missions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Infos personnelles
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Documents
          </TabsTrigger>
          {user?.accountType === 'company' && (
            <TabsTrigger value="company" className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              Société
            </TabsTrigger>
          )}
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Sécurité
          </TabsTrigger>
        </TabsList>

        {/* Personal Info Tab */}
        <TabsContent value="personal">
          <Card>
            <CardHeader>
              <CardTitle>Informations personnelles</CardTitle>
              <CardDescription>Vos informations de contact</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input
                    name="firstName"
                    value={personalData.firstName}
                    onChange={handlePersonalChange}
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input
                    name="lastName"
                    value={personalData.lastName}
                    onChange={handlePersonalChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="email"
                    type="email"
                    value={personalData.email}
                    onChange={handlePersonalChange}
                    disabled={!isEditing}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="phone"
                    value={personalData.phone}
                    onChange={handlePersonalChange}
                    disabled={!isEditing}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                {isEditing ? (
                  <>
                    <Button onClick={handleSavePersonal} disabled={isSaving}>
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Enregistrer
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>Annuler</Button>
                  </>
                ) : (
                  <Button onClick={() => setIsEditing(true)}>Modifier</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="space-y-6">
            {/* Permit Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Permis de conduire
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Type de permis *</Label>
                    <Select 
                      value={documentsData.permitType} 
                      onValueChange={(value) => setDocumentsData(prev => ({ ...prev, permitType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {permitTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Numéro de permis *</Label>
                    <Input
                      name="permitNumber"
                      placeholder="CI-XXXXXXXX"
                      value={documentsData.permitNumber}
                      onChange={handleDocumentsChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date d'expiration *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        name="permitExpirationDate"
                        type="date"
                        value={documentsData.permitExpirationDate}
                        onChange={handleDocumentsChange}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CNI Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Carte Nationale d'Identité
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Numéro CNI *</Label>
                  <Input
                    name="cniNumber"
                    placeholder="CI00012345"
                    value={documentsData.cniNumber}
                    onChange={handleDocumentsChange}
                  />
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSaveDocuments} disabled={isSaving} className="bg-[#1db584]">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Enregistrer les documents
            </Button>
          </div>
        </TabsContent>

        {/* Company Tab */}
        {user?.accountType === 'company' && (
          <TabsContent value="company">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Informations société
                </CardTitle>
                <CardDescription>Documents légaux de votre société (optionnel)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de la société</Label>
                  <Input
                    name="companyName"
                    value={companyData.companyName}
                    onChange={handleCompanyChange}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>RCCM</Label>
                    <Input
                      name="rccm"
                      placeholder="Registre du Commerce"
                      value={companyData.rccm}
                      onChange={handleCompanyChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>DFE</Label>
                    <Input
                      name="dfe"
                      placeholder="Déclaration Fiscale"
                      value={companyData.dfe}
                      onChange={handleCompanyChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Carte de transport</Label>
                    <Input
                      name="transportCard"
                      value={companyData.transportCard}
                      onChange={handleCompanyChange}
                    />
                  </div>
                </div>
                <Button onClick={handleSaveCompany} disabled={isSaving} className="mt-4">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Enregistrer
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Sécurité du compte
              </CardTitle>
              <CardDescription>Modifier votre mot de passe</CardDescription>
            </CardHeader>
            <CardContent>
              {isChangingPassword ? (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Mot de passe actuel</Label>
                    <Input
                      name="oldPassword"
                      type="password"
                      value={passwordData.oldPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nouveau mot de passe</Label>
                    <Input
                      name="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirmer le nouveau mot de passe</Label>
                    <Input
                      name="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Changer le mot de passe
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsChangingPassword(false)}>
                      Annuler
                    </Button>
                  </div>
                </form>
              ) : (
                <Button onClick={() => setIsChangingPassword(true)}>
                  Modifier mon mot de passe
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DriverProfile;
